import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import type { CreateRosterMembershipRequest } from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/rostermemberships/v1/roster_memberships_api';

import { fetchProgramBySlug } from 'bundles/internal-enterprise/utils/apiUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';

import 'css!./__styles__/EnterpriseToolsHome';

const createRosterMembership = (body: CreateRosterMembershipRequest) =>
  fetch('/api/grpc/enterpriseuser/rostermemberships/v1/RosterMembershipsAPI/CreateRosterMembership', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  });

class JoinProgram extends React.Component<
  {
    userId: string;
    refreshData: () => void;
    closeModal: () => void;
    email: string;
    programMembershipsWithPrograms: { programId: string }[];
  },
  {
    programId: string;
    programSlug: string;
    fullName: string;
    email: string;
    loading: boolean;
    errorMessage: string;
    fetchErrorMessage: string;
  }
> {
  static contextTypes = {
    executeMutation: PropTypes.func,
  };

  declare context: LegacyContextType<typeof JoinProgram.contextTypes>;

  constructor(props: $TSFixMe) {
    super(props);
    const { programMembershipsWithPrograms, email } = props;

    const maybeMembershipWithUserData =
      programMembershipsWithPrograms &&
      programMembershipsWithPrograms.find((membership: $TSFixMe) => membership.externalUserData);
    const maybeName = maybeMembershipWithUserData && maybeMembershipWithUserData.externalUserData.definition.fullName;

    this.state = {
      programId: '',
      programSlug: '',
      fullName: maybeName || '',
      email: email || '',
      loading: false,
      errorMessage: '',
      fetchErrorMessage: '',
    };
  }

  handleUpdateText = (e: $TSFixMe) => {
    // @ts-expect-error TSMIGRATION-5.0
    this.setState({
      [e.target.name]: e.target.value,
      errorMessage: '',
      fetchErrorMessage: '',
    });
  };

  static mandatoryFields = ['programId', 'fullName', 'email'];

  createProgramMembership = () => {
    const { userId, programMembershipsWithPrograms } = this.props;
    const { programId, email, fullName } = this.state;

    const json: CreateRosterMembershipRequest = {
      userId: parseInt(userId, 10),
      programId,
      userData: {
        email,
        fullName,
      },
      sendProgramWelcomeEmail: true,
    };

    const alreadyInvitedOrMember = programMembershipsWithPrograms?.some((p) => p.programId === json.programId);

    if (alreadyInvitedOrMember) {
      this.setState({
        errorMessage: 'It appears the user is already invited or part of the program.',
      });
      return;
    }

    this.setState({ loading: true });

    createRosterMembership(json)
      .then((r) => {
        if (r.ok) {
          this.props.closeModal();
          this.props.refreshData();
        } else {
          r.json().then((data) => {
            this.setState({
              errorMessage: JSON.parse(data.message).description ?? 'Something went wrong.',
              loading: false,
            });
          });
        }
      })
      .catch(() => {
        this.setState({
          errorMessage: 'Something went wrong.',
          loading: false,
        });
      });
  };

  fetchProgram = () => {
    this.setState({ loading: true });
    fetchProgramBySlug(this.state.programSlug)
      .then(
        ({ elements }) => {
          if (elements && elements.length) {
            this.setState({
              fetchErrorMessage: '',
              programId: elements[0].id,
            });
          }
        },
        (failure) => {
          this.setState({
            fetchErrorMessage: failure.responseJSON.message || failure.status + ' ' + failure.statusText,
            programId: '',
          });
        }
      )
      .finally(() => this.setState({ loading: false }));
  };

  render() {
    const { programSlug, programId, fullName, email, loading, errorMessage, fetchErrorMessage } = this.state;
    return (
      <div className="rc-JoinProgram">
        <div>
          <TextBox
            className="inline fixed-size-textbox"
            name="programSlug"
            placeholder="Program Slug"
            onUpdateField={this.handleUpdateText}
            currentValue={programSlug}
          />
          <Button
            className="inline"
            onClick={this.fetchProgram}
            text="Get Program ID"
            btnStyle="info"
            disabled={!programSlug || loading}
          />
          {fetchErrorMessage && <div className="inline error-message">{fetchErrorMessage}</div>}
        </div>
        <TextBox
          name="programId"
          placeholder="Program ID"
          onUpdateField={this.handleUpdateText}
          currentValue={programId}
        />
        <TextBox
          name="fullName"
          placeholder="Full Name"
          onUpdateField={this.handleUpdateText}
          currentValue={fullName}
        />
        <TextBox name="email" placeholder="Email" onUpdateField={this.handleUpdateText} currentValue={email} />
        <br />
        <div>
          <Button
            className="inline"
            onClick={this.createProgramMembership}
            text="Add to program"
            btnStyle="info"
            disabled={!email || !fullName || !programId}
          />
          {errorMessage && <div className="inline error-message">{errorMessage}</div>}
        </div>
      </div>
    );
  }
}

export default JoinProgram;
